<div class="sidebar-container" (mouseenter)="onMouseEnter()" (mouseleave)="onMouseLeave()">
  <!-- Header: logo row + arrow row -->
  <header class="sidebar-header">
    <!-- Row 1: Logo and Title -->
    <div class="logo-title-placeholder">
      <div class="logo-wrapper">
        <img src="images/gopher.svg" alt="Gopher for Chrome Premium Logo" class="logo" />
      </div>
      <span class="title" *ngIf="sidebarState !== 'collapsed'">Gopher for Chrome Premium</span>
    </div>

    <!-- Row 2: Arrow button (right-aligned when expanded, centered when collapsed) -->
    <div class="arrow-row">
      <button
        type="button"
        class="arrow-button"
        (click)="onArrowClick()"
        [attr.aria-label]="
          sidebarState === 'expanded' ? 'Close sidebar'
            : sidebarState === 'peeking' ? 'Pin sidebar open'
            : 'Open sidebar'
        "
        [attr.aria-pressed]="sidebarState === 'expanded'"
        [matTooltip]="
          sidebarState === 'expanded' ? 'Close sidebar'
            : sidebarState === 'peeking' ? 'Pin sidebar open'
            : 'Open sidebar'
        "
        matTooltipPosition="right"
        matTooltipClass="sidebar-tooltip"
        data-testid="sidebar-arrow-button"
      >
        <lib-cdw-icon [icon]="arrowIcon"></lib-cdw-icon>
      </button>
    </div>
  </header>

  <!-- Scrollable Navigation Body -->
  <nav class="sidebar-nav">
    <ul>
      <ng-container *ngTemplateOutlet="recursiveList; context: { $implicit: navigationItems }"></ng-container>
    </ul>
  </nav>

  <!-- Pinned Footer -->
  <footer class="sidebar-footer">
    <!-- Job Indicator -->
    <app-job-indicator
      *ngIf="showJobIndicator"
      [sidebarExpanded]="sidebarState !== 'collapsed'"
      [isPanelOpen]="jobsPanelOpen"
      (panelToggle)="jobsPanelToggle.emit()"
    ></app-job-indicator>

    <!-- Account: 'profile' mode links to the in-app account page. -->
    <a
      *ngIf="accountMode === 'profile'"
      routerLink="/account-profile"
      class="account-link"
      routerLinkActive="active-link font-bold"
      (click)="onLeafItemClick()"
      [matTooltip]="'Account Profile'"
      [matTooltipDisabled]="sidebarState !== 'collapsed'"
      matTooltipPosition="right"
      matTooltipClass="sidebar-tooltip"
    >
      <img
        [src]="userInfo?.avatarUrl || 'assets/placeholder-avatar.png'"
        referrerpolicy="no-referrer"
        alt="User Avatar"
        class="user-avatar"
      />
      <span class="nav-text font-normal" [class.nav-text--hidden]="sidebarState === 'collapsed'">Account Profile</span>
    </a>

    <!-- Account: 'logout' mode (onboarding) opens a menu with email + Sign out. -->
    <div *ngIf="accountMode === 'logout'" class="account-menu">
      <button
        type="button"
        class="account-link account-trigger"
        data-testid="onboarding-user-menu-trigger"
        [attr.aria-expanded]="accountMenuOpen"
        aria-haspopup="menu"
        [matTooltip]="'Account'"
        [matTooltipDisabled]="sidebarState !== 'collapsed'"
        matTooltipPosition="right"
        matTooltipClass="sidebar-tooltip"
        (click)="toggleAccountMenu()"
      >
        <img
          *ngIf="accountPhotoUrl; else accountInitialTpl"
          [src]="accountPhotoUrl"
          referrerpolicy="no-referrer"
          alt="Account"
          class="user-avatar"
        />
        <ng-template #accountInitialTpl>
          <span class="user-avatar user-avatar--initial">{{ accountInitial }}</span>
        </ng-template>
        <span
          class="nav-text font-normal"
          [class.nav-text--hidden]="sidebarState === 'collapsed'"
          >Account</span
        >
      </button>

      <div *ngIf="accountMenuOpen" class="account-dropdown" role="menu">
        <div
          *ngIf="accountEmail"
          class="account-dropdown-email"
          data-testid="onboarding-user-email"
        >
          {{ accountEmail }}
        </div>
        <button
          type="button"
          class="account-dropdown-item"
          role="menuitem"
          data-testid="onboarding-sign-out"
          (click)="onSignOut()"
        >
          Sign out
        </button>
      </div>

      <!-- Click-outside closes the menu. -->
      <div
        *ngIf="accountMenuOpen"
        class="account-backdrop"
        data-testid="onboarding-user-menu-backdrop"
        (click)="closeAccountMenu()"
      ></div>
    </div>
  </footer>
</div>

<!-- Recursive Template for Navigation Items -->
<ng-template #recursiveList let-items>
  <li *ngFor="let item of items" [class.is-expanded]="item.isExpanded">
    <!-- Case 1: Item is a Link -->
    <a
      *ngIf="item.route && !item.children && !item.externalLink"
      [routerLink]="item.route"
      class="h-[72px]"
      [class.active-link]="item.isActive"
      (click)="onLeafItemClick()"
      [matTooltip]="item.title"
      [matTooltipDisabled]="sidebarState !== 'collapsed'"
      matTooltipPosition="right"
      matTooltipClass="sidebar-tooltip"
    >
      <lib-cdw-icon class="nav-icon" *ngIf="item.icon" [icon]="item.icon"></lib-cdw-icon>
      <span class="nav-bullet" *ngIf="!item.icon">•</span>
      <span class="nav-text" [class.nav-text--hidden]="sidebarState === 'collapsed'">{{ item.title }}</span>
    </a>

    <!-- Case 2: Item is an External Link -->
    <a
      *ngIf="item.route && item.externalLink"
      [href]="item.route"
      target="_blank"
      rel="noopener noreferrer"
      class="h-[72px]"
      [attr.data-testid]="sidebarTestId(item)"
      (click)="onLeafItemClick()"
      [matTooltip]="item.title"
      [matTooltipDisabled]="sidebarState !== 'collapsed'"
      matTooltipPosition="right"
      matTooltipClass="sidebar-tooltip"
    >
      <lib-cdw-icon class="nav-icon" *ngIf="item.icon" [icon]="item.icon"></lib-cdw-icon>
      <span class="nav-bullet" *ngIf="!item.icon">•</span>
      <span class="nav-text" [class.nav-text--hidden]="sidebarState === 'collapsed'">{{ item.title }}</span>
      <lib-cdw-icon class="external-icon" *ngIf="sidebarState !== 'collapsed'" icon="open_in_new"></lib-cdw-icon>
    </a>

    <!-- Case 3: Item is Expandable (has children) -->
    <button
      type="button"
      *ngIf="item.children && item.children.length > 0"
      (click)="toggleItemExpansion(item)"
      [attr.aria-expanded]="item.isExpanded"
      [class.active-link]="item.isActive"
      class="h-[72px]"
      [matTooltip]="item.title"
      [matTooltipDisabled]="sidebarState !== 'collapsed'"
      matTooltipPosition="right"
      matTooltipClass="sidebar-tooltip"
    >
      <lib-cdw-icon class="nav-icon" *ngIf="item.icon" [icon]="item.icon"></lib-cdw-icon>
      <span class="nav-bullet" *ngIf="!item.icon">•</span>
      <span class="nav-text" [class.nav-text--hidden]="sidebarState === 'collapsed'">{{ item.title }}</span>
      <lib-cdw-icon
        class="expand-icon"
        *ngIf="sidebarState !== 'collapsed'"
        [icon]="item.isExpanded ? 'expand_less' : 'expand_more'"
      >
      </lib-cdw-icon>
    </button>

    <!-- Recursive Call for Children -->
    <ul
      class="sub-menu"
      *ngIf="item.children && item.children.length > 0 && item.isExpanded && sidebarState !== 'collapsed'"
    >
      <ng-container *ngTemplateOutlet="recursiveList; context: { $implicit: item.children }"></ng-container>
    </ul>
  </li>
</ng-template>
